/**
 * Headless state for the services (products) list: `GET /api/admin/products`
 * plus the delete flow with its error-code mapping. Returns no JSX - web_admin
 * and web_crm render their own chrome on top of exactly this shape.
 */
import { useCallback, useEffect, useState } from "react";
import { makeT } from "../i18n";
import type { ApiClient, Locale, ProductDTO } from "../types";
import { errCodeStr } from "./shared";

export interface UseProductsOptions {
  api: ApiClient;
  locale: Locale;
  /** Fires after a successful delete, so a master-detail chrome can drop its selection. */
  onDeleted?: (product: ProductDTO) => void;
}

export interface UseProductsResult {
  products: ProductDTO[];
  loading: boolean;
  /** Re-fetch the list (also called by the chrome after a create/edit). */
  reload: () => Promise<void>;
  /** Product waiting for delete confirmation; `null` = the confirm dialog is closed. */
  deleteTarget: ProductDTO | null;
  setDeleteTarget: (product: ProductDTO | null) => void;
  /** Open the confirm dialog for `product` and clear any previous error. */
  askDelete: (product: ProductDTO) => void;
  deleting: boolean;
  /** Translated message for the last failed delete, or `null`. */
  deleteErr: string | null;
  doDelete: () => Promise<void>;
}

export function useProducts({ api, locale, onDeleted }: UseProductsOptions): UseProductsResult {
  const t = makeT(locale);
  const [products, setProducts] = useState<ProductDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<ProductDTO | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setProducts(await api.get<ProductDTO[]>("/admin/products"));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function askDelete(product: ProductDTO) {
    setDeleteErr(null);
    setDeleteTarget(product);
  }

  async function doDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleting(true);
    setDeleteErr(null);
    try {
      await api.del(`/admin/products/${target.code}`);
      setDeleteTarget(null);
      onDeleted?.(target);
      await reload();
    } catch (e) {
      const code = errCodeStr(e);
      setDeleteErr(
        code === "product_in_use" ? t("products.err.inUse")
        : code === "platform_product_protected" ? t("products.err.platformProtected")
        : t("products.err.generic"),
      );
    } finally {
      setDeleting(false);
    }
  }

  return {
    products, loading, reload,
    deleteTarget, setDeleteTarget, askDelete, deleting, deleteErr, doDelete,
  };
}
